import { useState, useEffect } from "react";
import { UserPlus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function AddContactDialog({
  targets, getSubtypesForTarget, onAddLead,
  preselectedTargetId, preselectedSubtypeId, context = "targets",
}) {
  const [open, setOpen] = useState(false);
  const [targetId, setTargetId] = useState(preselectedTargetId || targets[0]?.id || "");
  const [subtypeId, setSubtypeId] = useState(preselectedSubtypeId || "");
  const [businessName, setBusinessName] = useState("");
  const [contactPerson, setContactPerson] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetId(preselectedTargetId || targets[0]?.id || "");
      setSubtypeId(preselectedSubtypeId || "");
    }
  }, [open, preselectedTargetId, preselectedSubtypeId, targets]);

  const reset = () => {
    setBusinessName(""); setContactPerson(""); setPhone(""); setEmail(""); setAddress(""); setNotes("");
    setTargetId(preselectedTargetId || targets[0]?.id || ""); setSubtypeId(preselectedSubtypeId || "");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!businessName.trim() || !contactPerson.trim() || !targetId) return;
    setSubmitting(true);
    try {
      const result = await onAddLead({
        target_id: targetId,
        subtype_id: subtypeId || null,
        business_name: businessName.trim(),
        contact_person: contactPerson.trim(),
        phone: phone.trim(),
        email: email.trim(),
        address: address.trim(),
        notes: notes.trim() || null,
        is_target: true,
        is_event: context === "events",
        is_catering: context === "catering",
      });
      if (result) { reset(); setOpen(false); }
    } finally {
      setSubmitting(false);
    }
  };

  const availableSubtypes = getSubtypesForTarget(targetId);

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button className="gap-2" data-testid="add-contact-button">
          <UserPlus className="h-4 w-4" /> Add Contact
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary">
            <UserPlus className="h-5 w-5" /> Add New Contact
          </DialogTitle>
          <DialogDescription>Add a new contact to your targets database.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={targetId} onValueChange={(v) => { setTargetId(v); setSubtypeId(""); }}>
                <SelectTrigger data-testid="contact-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {targets.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Sub-Type</Label>
              <Select value={subtypeId} onValueChange={setSubtypeId}>
                <SelectTrigger data-testid="contact-subtype"><SelectValue placeholder="Select sub-type" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {availableSubtypes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="businessName">Business Name *</Label>
            <Input id="businessName" value={businessName} data-testid="contact-business"
              onChange={(e) => setBusinessName(e.target.value)} placeholder="Enter business name" required />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contactPerson">Contact Person *</Label>
              <Input id="contactPerson" value={contactPerson} data-testid="contact-person"
                onChange={(e) => setContactPerson(e.target.value)} placeholder="Full name" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="contact@business.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
              placeholder="Any additional notes about this contact..." />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" data-testid="submit-contact" disabled={submitting || !targetId || !businessName.trim() || !contactPerson.trim()}>
              {submitting ? "Adding..." : "Add Contact"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
